import { css } from '@emotion/react';
import { List, Hexagon, Plus } from '@phosphor-icons/react';
import { trpc, useQuery } from 'client/lib/trpc';
import { useAccount } from 'client/lib/User';
import { ButtonProps } from 'client/components/Button';
import { ButtonWithTrialBlock } from 'client/components/Button/ButtonWithTrialBlock';
import { Empty } from 'client/components/Empty';
import { Dashboard, Content } from 'client/components/Layout/Dashboard';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { Shade, Skeleton } from 'client/components/Skeleton';
import { Subheader } from 'client/components/Subheader';
import { mediaQuery } from 'client/Theme';
import { ProjectListItem } from './components/ProjectListItem';

export const ListSkeleton = () => {
  return (
    <Shade css={listStyle}>
      <Skeleton type="rect" height={287} />
      <Skeleton type="rect" height={287} />
      <Skeleton type="rect" height={287} />
      <Skeleton type="rect" height={287} />
    </Shade>
  );
};

const CreateProjectButton = (props: ButtonProps) => (
  <ButtonWithTrialBlock icon={<Plus weight="bold" />} href="../template/create" primary {...props}>
    Create Project
  </ButtonWithTrialBlock>
);

export const ProjectListPage = () => {
  const account = useAccount();

  const { data, isInitialLoading } = useQuery(trpc.project.list, { accountID: account?.id as number });

  const items = data?.items || [];

  const isEmpty = !items.length;
  const isLoading = isInitialLoading && isEmpty;

  return (
    <>
      <Subheader
        title="Projects"
        subtitle="View & edit available projects"
        icon={<ListItemFigure icon={<Hexagon />} ornament={<List />} />}
        actions={<CreateProjectButton />}
      />

      <Dashboard>
        <Content css={contentStyle}>
          {isEmpty && !isLoading && (
            <Empty css={emptyStyle}>
              <CreateProjectButton />
            </Empty>
          )}

          {isLoading && <ListSkeleton />}

          <ol css={listStyle}>
            {items.map((project) => (
              <ProjectListItem key={project.id} {...project} />
            ))}
          </ol>
        </Content>
      </Dashboard>
    </>
  );
};

const listStyle = css`
  margin: 0;
  padding: 0;
  padding-top: 32px;
  width: 100%;
  min-width: 0;
  list-style: none;

  ${mediaQuery.large(css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  `)}

  > a {
    max-width: 100%;
    overflow: hidden;
  }
`;

const contentStyle = css`
  margin-top: -2rem;

  ${mediaQuery.large(css`
    width: 100%;
  `)}
`;

const emptyStyle = css`
  margin-top: 32px;
`;
